import * as yup from 'yup'
import { type ArraySchema } from 'yup'

import { type TeamExternalIntegration } from 'types/api/status-checks/status-check'
import {
  appdynamicsApiHealthRuleViolationsUrlRegex,
  appdynamicsApplicationUrlRegex,
  appdynamicsBaseUrlRegex
} from 'utils/status-checks/status-check-integrations-util-appdynamics'
import {
  awsAuthUrlRegex,
  cloudwatchAlarmRegex
} from 'utils/status-checks/status-check-integrations-util-aws'
import { datadogMonitorRegex } from 'utils/status-checks/status-check-integrations-util-datadog'
import { datadogEUMonitorRegex } from 'utils/status-checks/status-check-integrations-util-datadog-eu'
import { datadogUS1FedMonitorRegex } from 'utils/status-checks/status-check-integrations-util-datadog-us1-fed'
import { datadogUS3MonitorRegex } from 'utils/status-checks/status-check-integrations-util-datadog-us3'
import { datadogUS5MonitorRegex } from 'utils/status-checks/status-check-integrations-util-datadog-us5'
import {
  dynatraceApiPniClusterRegex,
  dynatraceApiPniRegex,
  dynatraceApiProblemsRegex,
  dynatraceBaseUrlRegex,
  dynatraceEntityUrlRegex,
  dynatraceHostsUrlRegex,
  dynatraceMonitorsUrlRegex
} from 'utils/status-checks/status-check-integrations-util-dynatrace'
import {
  grafanaApiAlertsRegex,
  grafanaBaseUrlRegex,
  grafanaRegex
} from 'utils/status-checks/status-check-integrations-util-grafana'
import { newRelicRegex } from 'utils/status-checks/status-check-integrations-util-newrelic'
import { pagerDutyRegex } from 'utils/status-checks/status-check-integrations-util-pagerduty'
import { IntegrationType } from 'utils/team-external-integrations/team-external-integration-types'
import { ENDPOINT_URLS } from 'utils/third-party'

import type { LoadGeneratorForm } from 'types/api/services/load-generator'

const minimumRequiredRegex = new RegExp('^.*$')

export const allowedIntegrationURLs: Record<IntegrationType, RegExp[]> = {
  datadog: [datadogMonitorRegex, new RegExp(`^${ENDPOINT_URLS.datadog}$`)],
  datadog_us3: [
    datadogUS3MonitorRegex,
    new RegExp(`^${ENDPOINT_URLS.datadog_us3}$`)
  ],
  datadog_us5: [
    datadogUS5MonitorRegex,
    new RegExp(`^${ENDPOINT_URLS.datadog_us5}$`)
  ],
  datadog_eu: [
    datadogEUMonitorRegex,
    new RegExp(`^${ENDPOINT_URLS.datadog_eu}$`)
  ],
  datadog_us1_fed: [
    datadogUS1FedMonitorRegex,
    new RegExp(`^${ENDPOINT_URLS.datadog_us1_fed}$`)
  ],
  custom: [minimumRequiredRegex],
  load_generator_custom: [minimumRequiredRegex],
  pagerduty: [pagerDutyRegex, new RegExp(`^${ENDPOINT_URLS.pagerduty}$`)],
  grafana: [grafanaRegex, grafanaApiAlertsRegex, grafanaBaseUrlRegex],
  newrelic: [newRelicRegex, new RegExp(`^${ENDPOINT_URLS.newrelic}$`)],
  appdynamics: [
    appdynamicsApplicationUrlRegex,
    appdynamicsApiHealthRuleViolationsUrlRegex
  ],
  dynatrace: [
    dynatraceApiProblemsRegex,
    dynatraceMonitorsUrlRegex,
    dynatraceHostsUrlRegex,
    dynatraceEntityUrlRegex
  ],
  prometheus: [],
  aws: [awsAuthUrlRegex, cloudwatchAlarmRegex],
  k6: [new RegExp(`^${ENDPOINT_URLS.k6}$`)]
}

/*
 * PNI variants may have looser or entirely different regular expression matches
 * than their public (default) counterparts
 */
export const allowedIntegrationURLsWithPNI: Record<
  IntegrationType,
  RegExp[] | null
> = {
  datadog: null,
  datadog_us3: null,
  datadog_us5: null,
  datadog_eu: null,
  datadog_us1_fed: null,
  custom: null,
  load_generator_custom: null,
  pagerduty: null,
  grafana: [minimumRequiredRegex],
  newrelic: null,
  appdynamics: null,
  dynatrace: [
    ...allowedIntegrationURLs['dynatrace'],
    dynatraceApiPniRegex,
    dynatraceApiPniClusterRegex
  ],
  prometheus: null,
  aws: null,
  k6: null
}

export const generateTeamIntegrationValidationSchema = (
  skipHeaderValidation?: boolean,
  integrationType?: IntegrationType,
  reservedNames?: string[],
  savedIntegrations?: TeamExternalIntegration[]
) => {
  yup.addMethod<ArraySchema<any, any, any[], any[]>>(
    yup.array,
    'unique',
    function (message, mapper = (a) => a) {
      return this.test(
        'unique',
        message,
        (list) => list.length === new Set(list.map(mapper)).size
      )
    }
  )

  const sanitizedNames = reservedNames.map((name) => name.trim().toLowerCase())
  const sanitizedUrls = savedIntegrations.map((integration) =>
    integration.url?.toLowerCase()
  )
  const existingIntegrationNames = savedIntegrations.map(
    (integration) => integration.name
  )

  return yup.object().shape({
    name: yup
      .string()
      .required('Please enter the integration name')
      .min(3, 'Please enter the integration name')
      .notOneOf(
        existingIntegrationNames
          ? [...existingIntegrationNames, 'Custom', 'custom', 'Other']
          : [],
        'Integration name already exists'
      )
      .test(
        'is-name-not-reserved',
        'Please do not use reserved names',
        (value) => {
          return !sanitizedNames.includes(value?.trim().toLowerCase())
        }
      ),
    url: yup
      .string()
      .test('is-url-valid', 'URL is not valid', (value) => {
        try {
          new URL(value)
        } catch (e) {
          return false
        }

        if (integrationType === IntegrationType.Grafana) {
          return grafanaBaseUrlRegex.test(value)
        }

        if (integrationType === IntegrationType.Dynatrace) {
          return dynatraceBaseUrlRegex.test(value)
        }

        if (integrationType === IntegrationType.Appdynamics) {
          return appdynamicsBaseUrlRegex.test(value)
        }

        return Boolean(
          allowedIntegrationURLs[
            integrationType ||
              IntegrationType.Custom ||
              IntegrationType.LoadGeneratorCustom
          ].find((u) => u.test(value))
        )
      })
      .test('endpoint-already-used', 'Endpoint is already in use', (value) => {
        return !sanitizedUrls.includes(value?.trim().toLowerCase())
      }),
    headers: yup.array().when(() =>
      skipHeaderValidation
        ? yup.array().notRequired()
        : yup
            .array()
            .of(
              yup.object().shape({
                key: yup.string().required('Header key is required'),
                value: yup.string().required('Header value is required')
              })
            ) // @ts-ignore tried multiple yup schema extensions without success https://github.com/jquense/yup/issues/312
            .unique('Header key already exists', (a) => a.key)
    )
  })
}

export const generateLoadGeneratorWebhookValidationSchema = (
  values: LoadGeneratorForm
) => {
  return yup
    .object()
    .nullable(true)
    .shape({
      uri: values.privateNetwork
        ? yup.string().required('URL is required')
        : yup.string().required('URL is required').url('URL is not valid'),
      method: yup.string().required('Method is required'),
      payload: yup.string().when('method', (method) => {
        if (method !== 'GET') {
          return yup
            .string()
            .required('Request body is required')
            .test('is-json-valid', 'Request body is not valid', (value) => {
              try {
                JSON.parse(value)
              } catch (e) {
                return false
              }
              return true
            })
        }
      })
    })
}
